import React, { useState } from 'react';
import {
  IonContent,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonList,
  IonItem,
  IonLabel,
  IonIcon,
  IonAvatar,
  IonBackButton,
  IonText,
  IonModal,
  IonFooter,
  IonSpinner,
  IonInput,
  IonToast,
} from '@ionic/react';
import { useClerk, useUser, useAuth } from '@clerk/clerk-react';
import { useHistory } from 'react-router-dom';
import {
  logOutOutline,
  personOutline,
  listOutline,
  chatbubblesOutline,
  peopleOutline,
  notificationsOutline,
  chevronForwardOutline,
  albumsOutline,
  giftOutline,
  copyOutline,
  shareOutline,
} from 'ionicons/icons';
import { logActivity } from '../lib/logger';
import { apiUrl, apiHeaders } from '../lib/api';
import { usePageView } from '../hooks/useLoggerInit';
import { useI18n } from '../i18n/I18nProvider';
import { registerWebPushToken } from '../lib/pushNotifications';
import DraftMigrationRunner from '../components/DraftMigrationRunner';
import ReferralClaimRunner from '../components/ReferralClaimRunner';

interface NavTile {
  icon: string;
  titleKey: string;
  subtitleKey: string;
  path: string;
}

function DashboardRowIcon({
  icon,
  color,
}: {
  icon: string;
  color?: string;
}) {
  return (
    <span className="dashboard-row-icon" slot="start" aria-hidden="true">
      <IonIcon icon={icon} color={color} />
    </span>
  );
}

function DashboardListRow({
  icon,
  iconColor = 'primary',
  title,
  subtitle,
  onClick,
  labelColor,
  className,
}: {
  icon: string;
  iconColor?: string;
  title: string;
  subtitle: string;
  onClick: () => void;
  labelColor?: string;
  className?: string;
}) {
  return (
    <IonItem
      button
      detail
      lines="none"
      className={`dashboard-row${className ? ` ${className}` : ''}`}
      onClick={onClick}
    >
      <DashboardRowIcon icon={icon} color={iconColor} />
      <IonLabel color={labelColor}>
        <h3>{title}</h3>
        <p>{subtitle}</p>
      </IonLabel>
    </IonItem>
  );
}

const NAV_TILES: NavTile[] = [
  {
    icon: chatbubblesOutline,
    titleKey: 'dashboard.activity.openChat.title',
    subtitleKey: 'dashboard.activity.openChat.subtitle',
    path: '/chats',
  },
  {
    icon: albumsOutline,
    titleKey: 'dashboard.activity.openTopics.title',
    subtitleKey: 'dashboard.activity.openTopics.subtitle',
    path: '/topics',
  },
  {
    icon: peopleOutline,
    titleKey: 'dashboard.activity.contacts.title',
    subtitleKey: 'dashboard.activity.contacts.subtitle',
    path: '/contacts',
  },
  {
    icon: personOutline,
    titleKey: 'dashboard.quickActions.editProfile',
    subtitleKey: 'dashboard.activity.editProfile.subtitle',
    path: '/profile',
  },
];

const DashboardPage: React.FC = () => {
  const { signOut } = useClerk();
  const { user, isLoaded } = useUser();
  const history = useHistory();
  const { getToken } = useAuth();
  const { t } = useI18n();

  const [recentChats, setRecentChats] = React.useState<
    { id: string; name: string }[]
  >([]);
  const [recentChatsError, setRecentChatsError] = React.useState<string | null>(
    null
  );
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [referralLoading, setReferralLoading] = useState(false);
  const [referralShareUrl, setReferralShareUrl] = useState('');
  const [referralCount, setReferralCount] = useState(0);
  const [referralError, setReferralError] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  usePageView('dashboard');

  async function openReferralModal() {
    setShowReferralModal(true);
    setReferralLoading(true);
    setReferralError(null);
    try {
      const token = await getToken();
      const res = await fetch(apiUrl('/api/referrals/me'), {
        headers: {
          'Content-Type': 'application/json',
          ...apiHeaders(token),
        },
      });
      if (!res.ok) {
        throw new Error(`Failed (${res.status})`);
      }
      const data = (await res.json()) as {
        shareUrl: string;
        referralCount: number;
      };
      setReferralShareUrl(data.shareUrl);
      setReferralCount(data.referralCount ?? 0);
    } catch {
      setReferralError(t('api.errors.internal'));
      setReferralShareUrl('');
    } finally {
      setReferralLoading(false);
    }
  }

  async function handleCopyReferralLink() {
    if (!referralShareUrl) return;
    try {
      await navigator.clipboard.writeText(referralShareUrl);
      setToastMessage(t('referral.copied'));
    } catch {
      setToastMessage(t('referral.copied'));
    }
  }

  async function handleShareReferralLink() {
    if (!referralShareUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: t('referral.title'),
          url: referralShareUrl,
          text: t('referral.shareText'),
        });
        return;
      } catch {
        // fall through to copy
      }
    }
    await handleCopyReferralLink();
  }

  React.useEffect(() => {
    let isMounted = true;

    async function loadRecentChats() {
      if (!user?.id) return;
      try {
        setRecentChatsError(null);
        const token = await getToken();
        const params = new URLSearchParams({
          userId: user.id,
          eventType: 'chat_open',
          limit: '20',
        });
        const res = await fetch(apiUrl(`/api/logs?${params.toString()}`), {
          headers: {
            'Content-Type': 'application/json',
            ...apiHeaders(token),
          },
        });

        if (!res.ok) {
          throw new Error(`Failed to load recent chats (${res.status})`);
        }

        const json = (await res.json()) as {
          data: {
            id: string;
            eventType: string;
            metadata: Record<string, unknown>;
          }[];
        };

        const seen = new Set<string>();
        const chats: { id: string; name: string }[] = [];

        for (const log of json.data) {
          const meta = log.metadata as {
            conversationId?: string;
            conversationName?: string;
          };
          const cid = meta.conversationId;
          if (!cid || seen.has(cid)) continue;
          seen.add(cid);
          chats.push({
            id: cid,
            name: meta.conversationName || 'Chat',
          });
          if (chats.length >= 5) break;
        }

        if (isMounted) {
          setRecentChats(chats);
        }
      } catch (err) {
        if (isMounted) {
          setRecentChatsError(
            err instanceof Error
              ? err.message
              : t('dashboard.recentChats.error')
          );
        }
      }
    }

    loadRecentChats();

    return () => {
      isMounted = false;
    };
  }, [getToken, t, user?.id]);

  const handleSignOut = async () => {
    await logActivity('user_signed_out', { userId: user?.id });
    await signOut();
    history.push('/');
  };

  const handleEnablePush = async () => {
    try {
      const jwt = await getToken();
      if (!jwt) return;
      const result = await registerWebPushToken(jwt);
      if (result.status === 'registered') {
        setToastMessage(t('dashboard.push.enabled'));
        return;
      }
      if (result.status === 'denied') {
        setToastMessage(t('dashboard.push.denied'));
        return;
      }
      if (result.status === 'unsupported') {
        setToastMessage(t('dashboard.push.unsupported'));
        return;
      }
      setToastMessage(t('dashboard.push.failed'));
    } catch (err) {
      console.error('[dashboard] Failed to enable push notifications', err);
      setToastMessage(t('dashboard.push.failed'));
    }
  };

  const displayName =
    user?.firstName ||
    user?.fullName ||
    user?.primaryEmailAddress?.emailAddress ||
    '';

  if (!isLoaded) {
    return (
      <IonPage>
        <IonContent className="ion-padding">
          <p>{t('common.loading')}</p>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage className="dashboard-page">
      <IonHeader translucent>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/" text={t('common.actions.back')} />
          </IonButtons>
          <IonTitle>{t('dashboard.title')}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => history.push('/profile')} aria-label={t('profile.title')}>
              <IonAvatar className="dashboard-toolbar-avatar">
                {user?.imageUrl ? (
                  <img src={user.imageUrl} alt="" />
                ) : (
                  <div className="dashboard-toolbar-avatar__fallback">
                    <IonIcon icon={personOutline} />
                  </div>
                )}
              </IonAvatar>
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen className="vault-screen dashboard-content">
        <DraftMigrationRunner />
        <ReferralClaimRunner />

        <section className="dashboard-hero">
          <h1>{t('dashboard.greeting', { name: displayName })}</h1>
          <p>{t('dashboard.subtitle')}</p>
        </section>

        <section className="dashboard-nav-grid" aria-label={t('dashboard.nav.title')}>
          {NAV_TILES.map((tile) => (
            <button
              key={tile.path}
              type="button"
              className="dashboard-nav-tile"
              onClick={() => history.push(tile.path)}
            >
              <span className="dashboard-nav-tile__icon">
                <IonIcon icon={tile.icon} />
              </span>
              <span className="dashboard-nav-tile__title">{t(tile.titleKey)}</span>
              <span className="dashboard-nav-tile__subtitle">{t(tile.subtitleKey)}</span>
            </button>
          ))}
        </section>

        <section className="dashboard-section">
          <div className="dashboard-section__head">
            <h2>{t('dashboard.recentChats.title')}</h2>
            <IonButton fill="clear" size="small" onClick={() => history.push('/chats')}>
              {t('dashboard.recentChats.seeAll')}
              <IonIcon slot="end" icon={chevronForwardOutline} />
            </IonButton>
          </div>

          <IonList lines="none" className="dashboard-panel dashboard-row-list dashboard-settings-list">
            {recentChatsError && (
              <IonItem lines="none" className="dashboard-row-list__message">
                <IonLabel>
                  <IonText color="danger">{recentChatsError}</IonText>
                </IonLabel>
              </IonItem>
            )}
            {!recentChatsError && recentChats.length === 0 && (
              <IonItem lines="none" className="dashboard-row-list__empty">
                <DashboardRowIcon icon={chatbubblesOutline} color="medium" />
                <IonLabel>
                  <p>{t('dashboard.recentChats.empty')}</p>
                  <IonButton fill="outline" size="small" onClick={() => history.push('/chats')}>
                    {t('dashboard.recentChats.startChat')}
                  </IonButton>
                </IonLabel>
              </IonItem>
            )}
            {recentChats.map((chat) => (
              <DashboardListRow
                key={chat.id}
                icon={chatbubblesOutline}
                title={chat.name}
                subtitle={t('dashboard.recentChats.tapToReopen')}
                onClick={() => history.push(`/chat/${chat.id}`)}
              />
            ))}
          </IonList>
        </section>

        <section className="dashboard-section dashboard-section--footer">
          <div className="dashboard-section__head">
            <h2>{t('dashboard.quickActions.title')}</h2>
          </div>

          <IonList lines="none" className="dashboard-panel dashboard-row-list dashboard-settings-list">
            <DashboardListRow
              icon={giftOutline}
              title={t('dashboard.quickActions.inviteFriends')}
              subtitle={t('dashboard.quickActions.inviteFriends.subtitle')}
              onClick={() => void openReferralModal()}
            />
            <DashboardListRow
              icon={notificationsOutline}
              title={t('dashboard.quickActions.enablePush')}
              subtitle={t('dashboard.quickActions.enablePush.subtitle')}
              onClick={handleEnablePush}
            />
            <DashboardListRow
              icon={listOutline}
              title={t('dashboard.activity.viewLogs.title')}
              subtitle={t('dashboard.activity.viewLogs.subtitle')}
              onClick={() => history.push('/activity')}
            />
            <DashboardListRow
              icon={logOutOutline}
              iconColor="danger"
              labelColor="danger"
              className="dashboard-sign-out"
              title={t('dashboard.quickActions.signOut')}
              subtitle={t('dashboard.quickActions.signOut.subtitle')}
              onClick={handleSignOut}
            />
          </IonList>
        </section>

        <IonModal
          className="vault-glass-modal"
          isOpen={showReferralModal}
          onDidDismiss={() => setShowReferralModal(false)}
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>{t('referral.title')}</IonTitle>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {referralLoading && (
              <div className="ion-text-center ion-padding">
                <IonSpinner name="crescent" />
              </div>
            )}
            {!referralLoading && referralError && (
              <IonText color="danger">
                <p>{referralError}</p>
              </IonText>
            )}
            {!referralLoading && !referralError && (
              <>
                <IonItem lines="none" className="vault-field-item">
                  <IonLabel position="stacked">
                    {t('referral.shareUrlLabel')}
                  </IonLabel>
                  <IonInput value={referralShareUrl} readonly />
                </IonItem>
                <IonText color="medium">
                  <p className="ion-padding-horizontal">
                    {t('referral.count', { count: referralCount })}
                  </p>
                </IonText>
              </>
            )}
          </IonContent>
          <IonFooter>
            <IonToolbar>
              <IonButtons slot="start">
                <IonButton onClick={() => setShowReferralModal(false)}>
                  {t('common.actions.close')}
                </IonButton>
              </IonButtons>
              <IonButtons slot="end">
                <IonButton
                  onClick={() => void handleCopyReferralLink()}
                  disabled={!referralShareUrl || referralLoading}
                >
                  <IonIcon slot="start" icon={copyOutline} />
                  {t('referral.copy')}
                </IonButton>
                <IonButton
                  onClick={() => void handleShareReferralLink()}
                  disabled={!referralShareUrl || referralLoading}
                >
                  <IonIcon slot="start" icon={shareOutline} />
                  {t('referral.share')}
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonFooter>
        </IonModal>

        <IonToast
          isOpen={!!toastMessage}
          message={toastMessage ?? ''}
          duration={1800}
          onDidDismiss={() => setToastMessage(null)}
        />

        <style>{`
          .dashboard-content {
            --dashboard-inset-x: 16px;
            --dashboard-icon-slot: 22px;
            --dashboard-icon-gap: 16px;
            --padding-top: 8px;
            --padding-start: var(--dashboard-inset-x);
            --padding-end: var(--dashboard-inset-x);
            padding-bottom: calc(24px + env(safe-area-inset-bottom, 0px));
          }

          .dashboard-toolbar-avatar {
            width: 32px;
            height: 32px;
          }

          .dashboard-toolbar-avatar__fallback {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(var(--ion-color-medium-rgb), 0.2);
            border-radius: 50%;
            color: var(--ion-color-medium);
          }

          .dashboard-hero {
            margin-bottom: 20px;
          }

          .dashboard-hero h1 {
            margin: 0 0 6px;
            font-size: 26px;
            font-weight: 700;
            line-height: 1.2;
          }

          .dashboard-hero p {
            margin: 0;
            color: var(--ion-color-medium);
            font-size: 14px;
            line-height: 1.45;
          }

          .dashboard-nav-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 12px;
            margin-bottom: 24px;
          }

          .dashboard-nav-tile {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
            border: 1px solid rgba(var(--ion-color-medium-rgb), 0.22);
            border-radius: 14px;
            background: rgba(8, 19, 41, 0.55);
            color: inherit;
            text-align: left;
            cursor: pointer;
            transition: border-color 0.15s ease, background 0.15s ease;
          }

          .dashboard-nav-tile:active {
            background: rgba(var(--ion-color-primary-rgb), 0.12);
            border-color: rgba(var(--ion-color-primary-rgb), 0.35);
          }

          .dashboard-nav-tile__icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: var(--dashboard-icon-slot);
            height: var(--dashboard-icon-slot);
            margin-bottom: 4px;
            color: var(--ion-color-primary);
          }

          .dashboard-nav-tile__icon ion-icon {
            font-size: var(--dashboard-icon-slot);
          }

          .dashboard-nav-tile__title {
            font-size: 16px;
            font-weight: 600;
            line-height: 1.2;
          }

          .dashboard-nav-tile__subtitle {
            font-size: 12px;
            line-height: 1.35;
            color: var(--ion-color-medium);
          }

          .dashboard-section {
            margin-bottom: 24px;
          }

          .dashboard-section__head {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 8px;
            margin-bottom: 10px;
          }

          .dashboard-section__head h2 {
            margin: 0;
            font-size: 18px;
            font-weight: 600;
          }

          .dashboard-section__head ion-button {
            margin: 0;
            text-transform: none;
            font-size: 13px;
          }

          .dashboard-row-list__empty ion-label p {
            margin: 0 0 8px;
          }

          .dashboard-row-list__message ion-label {
            white-space: normal;
          }
        `}</style>
      </IonContent>
    </IonPage>
  );
};

export default DashboardPage;
